import ReplayIcon from '@mui/icons-material/Replay';
import ErrorAlerts from 'components/generic/ErrorAlerts';
import SkeletonBox from 'components/generic/SkeletonBox';
import { NoAccess } from 'components/NoAccess/NoAccess';
import { useCosmosQueryClient } from 'providers/clients/CosmosQueryClientProvider';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { CosmosEditorialContextProvider } from 'providers/contexts/CosmosEditorialContext';
import { memo, Suspense, useCallback, useMemo } from 'react';
import {
  InfiniteListBase,
  SortPayload,
  useInfinitePaginationContext,
  useListContext,
} from 'react-admin';
import { useTranslation } from 'react-i18next';
import { sanitizeEditorialFilter } from 'screens/editorial/Search/EditorialSearchView';
import { checkSearchEnabled } from 'components/Search/functions';
import { emdash } from 'utility/constants';
import {
  checkEditorialAccessPermission,
  checkSiteAdminPermission,
} from 'utility/permissions';
import { EditorialAssetSearchResultItem } from 'utility/types/dataProvider';
import {
  SearchModalBodyLayout,
  SearchModalCardViewLayout,
  SearchModalInputsLayout,
} from '../generic/SearchModalLayout';
import { useSearchModalContext } from '../SearchModal';
import { SearchModalCardsFragmentEditorial } from './SearchModalCardEditorial';
import { SearchModalDatagridEditorial } from './SearchModalDatagridEditorial';
import {
  SearchModalInputsBottomEditorial,
  SearchModalInputsLeftEditorial,
  SearchModalInputsRightEditorial,
} from './SearchModalInputsEditorial';

/**
 * RA InfiniteListBase Note:
 * RQ Search Query Keys v3 example:
 * ["editorial","getInfiniteList",{"filter":{},"pagination":{"page":1,"perPage":50},"sort":{...}}]
 */

/**
 * --------------------------------------------------------
 * Search Modal Body - Editorial
 * Contains:
 * - Suspense fallback
 * - Permissions view checks
 * - Editorial context provider
 * - RA InfiniteListBase context
 * - Inner layout with Inputs + Results View
 * --------------------------------------------------------
 */
export const SearchModalBodyEditorial = memo(() => {
  const { sessionConfig } = useCosmos();

  // Query client and filters
  const { queryClient } = useCosmosQueryClient();
  const { dbFilters, resource } = useSearchModalContext();
  const { sort: debouncedSort, dir: debouncedOrder, ...debouncedFilter } = dbFilters;

  // Formatted / sanitized filter for api call
  const sanitizedFilter = useMemo(() => {
    const sanitized = sanitizeEditorialFilter(debouncedFilter);
    return sanitized;
  }, [debouncedFilter]);

  // Permissions Checks...
  // minimums to call: /editorial/asset/search/
  const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
  const { granted: userHasEditorial, name: editorialSettingsPermName } =
    checkEditorialAccessPermission(sessionConfig);
  const hasEditorialPerms = Boolean(userIsAdmin || userHasEditorial);

  // No access case
  if (!hasEditorialPerms) {
    return <NoAccess permName={editorialSettingsPermName} />;
  }

  return (
    <Suspense
      fallback={
        <SkeletonBox
          width="100%"
          height="100%"
        />
      }
    >
      <CosmosEditorialContextProvider>
        <InfiniteListBase
          resource={resource}
          disableAuthentication={false}
          disableSyncWithLocation={true} // not using RA UI components
          storeKey={false} // not using RA UI components
          exporter={false}
          perPage={50}
          sort={
            {
              field: debouncedSort,
              order: debouncedOrder,
            } as SortPayload
          }
          filter={sanitizedFilter}
          queryOptions={{
            enabled: checkSearchEnabled({
              filter: sanitizedFilter,
              resource: 'editorial',
            }).isEnabled,
            retry: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            // Note: gc/stale 0 fetches fresh each time the modal opens/closes
            gcTime: 0,
            staleTime: 0,
            onSettled: (data, error) => {
              // react-admin rq cache clear for 'partial record' issue
              // RA assumes getList() returns a subset of getOne() data
              // so it'll try to preload the getOne() for edit screens
              // with the data from the list incorrectly, remove rq cache.
              queryClient.removeQueries({ queryKey: ['editorial', 'getOne'] });
            },
          }}
        >
          <SearchModalBodyLayout
            inputsComponent={
              <SearchModalInputsLayout
                leftComponent={<SearchModalInputsLeftEditorial />}
                rightComponent={<SearchModalInputsRightEditorial />}
                bottomComponent={<SearchModalInputsBottomEditorial />}
              />
            }
            resultsComponent={<SearchModalResultsEditorial />}
          />
        </InfiniteListBase>
      </CosmosEditorialContextProvider>
    </Suspense>
  );
});
SearchModalBodyEditorial.displayName = 'SearchModalBodyEditorial';

/**
 * --------------------------------------------------------
 * Search Modal Results View - Editorial
 * - Handles loading/error states and view type rendering.
 * --------------------------------------------------------
 */
const SearchModalResultsEditorial = memo(() => {
  const { t } = useTranslation();
  const { isFetching, isLoading, error } =
    useListContext<EditorialAssetSearchResultItem>();
  const { isFetchingNextPage } = useInfinitePaginationContext();
  const { view, resetModalStates } = useSearchModalContext();
  const { queryClient } = useCosmosQueryClient();

  // Translation strings
  const strLoading = t('blox.general.loading');
  const strReset = t('blox.actions.reset');

  // Reset and trigger refetch
  const handleResetError = useCallback(() => {
    resetModalStates();
    queryClient.invalidateQueries({
      queryKey: ['editorial', 'getInfiniteList'],
    });
  }, [queryClient, resetModalStates]);

  // Display error if present
  if (error) {
    const errorStatus = (error as any)?.response?.status ?? 'unknown';
    const errorMessage = error?.message ?? emdash;

    return (
      <ErrorAlerts
        showDetails={true}
        severity="warning"
        buttonOnClick={handleResetError}
        buttonText={strReset}
        buttonIcon={<ReplayIcon />}
        sx={{ m: 1 }}
        errors={[
          {
            status: `${errorStatus}`,
            message: `${errorMessage}`,
            fullDetails: error,
          },
        ]}
      />
    );
  }

  // First fetch/load - loading with no data fetched yet
  // alt: const isFirstLoading = isLoading && data?.length === 0;
  const isFirstLoading = isLoading || (isFetching && !isFetchingNextPage);

  if (isFirstLoading) {
    return (
      <SkeletonBox
        text={strLoading}
        width="100%"
        height="100%"
      />
    );
  }

  /**
   * View Type Rendering
   * - Summary/List: Editorial datagrid
   * - Cards: Layout with Editorial cards
   */

  switch (view) {
    case 'summary':
    case 'list': {
      return <SearchModalDatagridEditorial />;
    }
    case 'card': {
      return (
        <SearchModalCardViewLayout
          cardsComponent={<SearchModalCardsFragmentEditorial />}
        />
      );
    }
    default: {
      return null;
    }
  }
});
SearchModalResultsEditorial.displayName = 'SearchModalResultsEditorial';
